import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/api";
import { useLang } from "@/context/LanguageContext";
import { useSite } from "@/context/SiteContext";
import { LogoMark } from "@/components/Logo";

const LABELS = {
  en: {
    title: "Edit website",
    sub: "Changes go live immediately. Blank fields fall back to the built-in content.",
    contact: "Contact details",
    waNumber: "WhatsApp number (digits only, e.g. 962790123456)",
    waDisplay: "WhatsApp number display",
    email: "Email",
    phone: "Phone display",
    heroEn: "Hero — English",
    heroAr: "Hero — العربية",
    line1: "Headline line 1",
    line2: "Headline line 2",
    sub: "Subtitle",
    images: "Images (paste URLs)",
    imgHero: "Hero image",
    imgChild: "Child tapping photo",
    imgGate: "School gate photo",
    imgCard: "Card close-up photo",
    gallery: "Gallery photo",
    save: "Save changes",
    saving: "Saving…",
    saved: "Saved — the website now shows your changes.",
    err: "Couldn't save — check your connection and try again.",
    back: "Back to dashboard",
  },
  ar: {
    title: "تعديل الموقع",
    sub: "التغييرات تظهر فورًا. الحقول الفارغة تعود للمحتوى الافتراضي.",
    contact: "بيانات التواصل",
    waNumber: "رقم واتساب (أرقام فقط، مثال: 962790123456)",
    waDisplay: "صيغة عرض الرقم",
    email: "البريد الإلكتروني",
    phone: "رقم الهاتف للعرض",
    heroEn: "القسم الرئيسي — English",
    heroAr: "القسم الرئيسي — العربية",
    line1: "العنوان الأول",
    line2: "العنوان الثاني",
    sub: "الوصف",
    images: "الصور (الصق روابط)",
    imgHero: "صورة القسم الرئيسي",
    imgChild: "صورة الطفل مع البطاقة",
    imgGate: "صورة بوابة المدرسة",
    imgCard: "صورة البطاقة",
    gallery: "صورة المعرض",
    save: "حفظ التغييرات",
    saving: "جارٍ الحفظ…",
    saved: "تم الحفظ — الموقع يعرض تغييراتك الآن.",
    err: "تعذر الحفظ — تحقق من الاتصال وحاول مجددًا.",
    back: "العودة إلى اللوحة",
  },
};

export default function AdminEdit() {
  const { lang, isAr } = useLang();
  const { setSettings } = useSite();
  const navigate = useNavigate();
  const L = LABELS[lang];
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/auth/me").catch(() => navigate("/admin", { replace: true }));
    api.get("/settings").then((r) => setForm(r.data || {})).catch(() => setForm({}));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    setSaved(false);
    setBusy(true);
    try {
      const { data } = await api.put("/settings", form);
      setSettings(data);
      setSaved(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || L.err);
    } finally {
      setBusy(false);
    }
  };

  const sections = [
    { title: L.contact, fields: [["whatsapp_number", L.waNumber, "text", "ltr"], ["whatsapp_display", L.waDisplay, "text", "ltr"], ["email", L.email, "text", "ltr"], ["phone", L.phone, "text", "ltr"]] },
    { title: L.heroEn, fields: [["hero_en_line1", L.line1], ["hero_en_line2", L.line2], ["hero_en_sub", L.sub, "textarea"]] },
    { title: L.heroAr, fields: [["hero_ar_line1", L.line1], ["hero_ar_line2", L.line2], ["hero_ar_sub", L.sub, "textarea"]] },
    { title: L.images, fields: [["img_hero", L.imgHero, "text", "ltr"], ["img_child_tap", L.imgChild, "text", "ltr"], ["img_gate", L.imgGate, "text", "ltr"], ["img_card", L.imgCard, "text", "ltr"], ["img_gallery_1", `${L.gallery} 1`, "text", "ltr"], ["img_gallery_2", `${L.gallery} 2`, "text", "ltr"], ["img_gallery_3", `${L.gallery} 3`, "text", "ltr"], ["img_gallery_4", `${L.gallery} 4`, "text", "ltr"]] },
  ];

  const Arrow = isAr ? ArrowRight : ArrowLeft;

  return (
    <div className="min-h-screen bg-creamdeep/60" data-testid="admin-edit-page">
      <header className="bg-pine text-cream">
        <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-5 sm:px-8">
          <div className="flex items-center gap-3">
            <LogoMark className="h-8 w-8" inverted />
            <span className="font-display text-lg font-bold">{L.title}</span>
          </div>
          <Link to="/admin/dashboard" className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm font-bold transition-colors hover:border-sun hover:text-sun" data-testid="admin-edit-back-link">
            <Arrow className="h-4 w-4" />
            {L.back}
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-10 sm:px-8">
        <p className="text-inksoft">{L.sub}</p>

        {form === null ? (
          <div className="grid place-items-center py-24" data-testid="admin-edit-loading">
            <Loader2 className="h-8 w-8 animate-spin text-pine" />
          </div>
        ) : (
          <form onSubmit={save} className="mt-8 space-y-6" data-testid="admin-edit-form">
            {sections.map((section) => (
              <div key={section.title} className="rounded-[1.6rem] border border-cardline bg-white p-6 sm:p-8">
                <h3 className="font-display text-xl font-bold text-ink">{section.title}</h3>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  {section.fields.map(([key, label, type, dir]) => (
                    <label key={key} className={`block ${type === "textarea" ? "sm:col-span-2" : ""}`}>
                      <span className="mb-1.5 block text-sm font-bold text-ink">{label}</span>
                      {type === "textarea" ? (
                        <textarea rows={2} dir={lang === "ar" ? "rtl" : "ltr"} data-testid={`admin-edit-${key}-input`} className="field-input resize-none" value={form[key] || ""} onChange={set(key)} />
                      ) : (
                        <input type={type} dir={dir || undefined} data-testid={`admin-edit-${key}-input`} className="field-input" value={form[key] || ""} onChange={set(key)} />
                      )}
                    </label>
                  ))}
                </div>
              </div>
            ))}

            {saved && (
              <p className="flex items-center gap-2 rounded-xl bg-pine-light px-4 py-3 text-sm font-bold text-pine" data-testid="admin-edit-success">
                <CheckCircle2 className="h-5 w-5" />
                {L.saved}
              </p>
            )}
            {err && (
              <p className="rounded-xl bg-terra-light px-4 py-3 text-sm font-semibold text-terra" data-testid="admin-edit-error">
                {err}
              </p>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="admin-edit-save-button"
              className="w-full rounded-xl bg-pine py-3.5 font-bold text-cream transition-colors hover:bg-pine-dark disabled:opacity-60 sm:w-auto sm:px-10"
            >
              {busy ? L.saving : L.save}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
